import { supabase } from "@/lib/supabase";
import type {
  AiChatTurnStatus,
  AiChatTurnStatusView,
  ChatMessage,
  ChatMessageWithAuthorView,
  Container,
  CreateRoomInput,
} from "@shared/types/supabase.types.ts";
import type { RealtimeChannel } from "@supabase/supabase-js";

export type MessageInsert = Omit<ChatMessage, "created_at" | "metadata"> & {
  created_at?: string;
  /**
   * CR-69 agent-provenance marker. Only the Kairo MCP server sets it (on
   * document comments); a chat message a human sends leaves it NULL.
   */
  metadata?: ChatMessage["metadata"];
};

/**
 * The two chat-like container types (CR-25):
 * - "room"         → AI Chat (AI-first: Kairo replies without being @mentioned)
 * - "message_room" → Message room (human-first: AI replies only when @mentioned)
 */
export type RoomContainerType = "room" | "message_room";

export type ChatRoomInsert = {
  name: string;
  slug: string;
  description?: string | null;
  logo_url?: string | null;
  data_eu?: boolean | null;
  is_shared?: boolean;
  container_type?: RoomContainerType;
};

/**
 * List chat-like rooms under a parent container.
 * Defaults to AI Chats ("room"); pass "message_room" for Message rooms.
 */
export async function listChatRooms(
  parentContainerId: string,
  containerType: RoomContainerType = "room",
): Promise<Container[]> {
  const { data, error } = await supabase
    .from("containers")
    .select("*")
    .eq("parent_container_id", parentContainerId)
    .eq("container_type", containerType)
    .order("name")
    .order("created_at");

  if (error) {
    throw new Error(`Failed to fetch chat rooms: ${error.message}`);
  }

  return data;
}

/** One participant of a message room, for deriving its Slack-style title (CR-67). */
export type MessageRoomMemberName = {
  container_id: string;
  actor_type: "user" | "ai" | "invited_user";
  user_id: string | null;
  display_name: string | null;
};

/**
 * Batch participant names for the given message rooms (one call for all rooms,
 * not one per room). Only rooms the caller is a member of return rows.
 */
export async function listMemberNamesForRooms(roomIds: string[]): Promise<MessageRoomMemberName[]> {
  if (!roomIds.length) return [];

  const { data, error } = await supabase.rpc("get_message_room_member_names", {
    p_room_ids: roomIds,
  });

  if (error) {
    throw new Error(`Failed to fetch room participants: ${error.message}`);
  }

  return data ?? [];
}

export async function listLastMessagesForRooms(
  roomIds: string[],
): Promise<ChatMessageWithAuthorView[]> {
  if (!roomIds.length) return [];

  const { data, error } = await supabase
    .from("message_with_author")
    .select("*")
    .in("room", roomIds)
    .order("created_at", { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch last messages: ${error.message}`);
  }

  return data;
}

/**
 * Create a new chat room under a parent container
 */
export async function createChatRoom(
  parentContainerId: string,
  input?: Partial<ChatRoomInsert>,
): Promise<Container> {
  const name = input?.name ?? "Untitled chat";
  const slug = input?.slug ?? `chat-${crypto.randomUUID()}`;

  const payload: CreateRoomInput = {
    p_name: name,
    p_slug: slug,
    p_parent_container_id: parentContainerId,
    p_description: input?.description ?? undefined,
    p_is_shared: input?.is_shared ?? false,
    p_container_type: input?.container_type ?? "room",
  };

  const { data, error } = await supabase.rpc("create_room", payload);

  if (error) {
    throw new Error(`Failed to create chat room: ${error.message}`);
  }

  return data;
}

/**
 * Add the project's Kairo persona to a freshly created room (CR-27, reused by
 * the CR-189 help chat). A project without a Kairo member leaves the room as
 * is; the lookup and the RPC errors are not fatal, matching the CR chat flow.
 */
export async function addProjectKairoToRoom(projectId: string, roomId: string): Promise<void> {
  const { data: kairoMember } = await supabase
    .from("container_members_ai")
    .select("ai_persona_id")
    .eq("container_id", projectId)
    .ilike("ai_persona_display_name", "kairo")
    .maybeSingle();

  if (kairoMember?.ai_persona_id) {
    await supabase.rpc("add_ai_member", {
      p_ai_persona_id: kairoMember.ai_persona_id,
      p_container_id: roomId,
    });
  }
}

/**
 * CR-97: rename a message room through the membership-gated RPC — anyone who
 * can OPEN the room can rename it (including project members who dropped into
 * a shared room and only hold role `member`, whom the generic containers
 * UPDATE policy would refuse). An empty name clears the stored title so the
 * room shows its derived default again. The RPC trims, collapses line breaks
 * and caps the name at 200 characters; every refusal (unknown id, AI chat,
 * deleted room, no access) is the same 42501 — do not branch on its message.
 */
export async function renameMessageRoom(roomId: string, name: string): Promise<Container> {
  const { data, error } = await supabase.rpc("rename_message_room", {
    p_room_id: roomId,
    p_name: name,
  });

  if (error) {
    throw new Error(`Failed to rename message room: ${error.message}`);
  }

  return data;
}

/**
 * Fetch messages for a specific room
 */
export async function getMessages(roomName: string): Promise<ChatMessageWithAuthorView[]> {
  const { data, error } = await supabase
    .from("message_with_author")
    .select("*")
    .eq("room", roomName)
    .order("created_at", { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch messages: ${error.message}`);
  }

  return data;
}

/**
 * Fetch message by ID
 */
export async function getMessage(messageId: string): Promise<ChatMessageWithAuthorView> {
  const { data, error } = await supabase
    .from("message_with_author")
    .select("*")
    .eq("id", messageId)
    .single();

  if (error) {
    throw new Error(`Failed to fetch message: ${error.message}`);
  }

  return data;
}

/**
 * Store a new message in the database
 */
export async function storeMessage(message: MessageInsert): Promise<ChatMessageWithAuthorView> {
  const messageInsert = {
    content: message.content,
    room: message.room,
    actor_id: message.actor_id,
    id: message.id,
  };

  const { data, error } = await supabase.from("messages").insert(messageInsert).select().single();

  if (error) {
    throw new Error(`Failed to store message: ${error.message}`);
  }

  return getMessage(data.id);
}

/**
 * Update an existing message's content
 */
export async function updateMessage(
  messageId: string,
  content: string,
): Promise<ChatMessageWithAuthorView> {
  const { error } = await supabase.from("messages").update({ content }).eq("id", messageId);

  if (error) {
    throw new Error(`Failed to update message: ${error.message}`);
  }

  return getMessage(messageId);
}

/**
 * Delete a message by ID
 */
export async function deleteMessage(messageId: string): Promise<void> {
  const { error } = await supabase.from("messages").delete().eq("id", messageId);

  if (error) {
    throw new Error(`Failed to delete message: ${error.message}`);
  }
}

/**
 * Create a realtime subscription for messages in a specific room
 */
export function createMessageSubscription(roomName: string, callback: (payload: any) => void) {
  const channel = supabase
    .channel(`messages-${roomName}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "messages",
        filter: `room=eq.${roomName}`,
      },
      callback,
    )
    .subscribe();

  return channel;
}

export function removeMessageSubscription(channel: RealtimeChannel) {
  return supabase.removeChannel(channel);
}

export type ChatMemory = {
  summary: string;
  message_count: number;
};

export async function getChatMemory(roomId: string): Promise<ChatMemory | null> {
  const { data, error } = await supabase
    .from("chat_memory")
    .select("summary, message_count")
    .eq("room_id", roomId)
    .maybeSingle();

  if (error) {
    console.warn("Failed to fetch chat memory:", error.message);
    return null;
  }

  return data;
}

/**
 * CR-141: the durable AI turns of a room the chat still has to show — queued
 * or running (a "thinking" bubble) and failed (an inline failure card) — from
 * the last day. Completed and cancelled turns have nothing left to render.
 */
const AI_TURN_WINDOW_MS = 24 * 60 * 60 * 1000;

export async function listOpenAiTurns(roomId: string): Promise<AiChatTurnStatusView[]> {
  const { data, error } = await supabase
    .from("ai_chat_turn_status")
    .select("*")
    .eq("room_id", roomId)
    .in("status", ["queued", "running", "failed"])
    .gte("created_at", new Date(Date.now() - AI_TURN_WINDOW_MS).toISOString())
    .order("created_at", { ascending: true })
    .order("turn_index", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/** CR-141: the current status of the given turns (any room the caller can read). */
export async function getAiTurnStatuses(turnIds: string[]): Promise<AiChatTurnStatusView[]> {
  if (!turnIds.length) return [];
  const { data, error } = await supabase.from("ai_chat_turn_status").select("*").in("id", turnIds);
  if (error) throw error;
  return data ?? [];
}

/** One realtime change of an AI turn row, reduced to what the client may read. */
export type AiTurnChange = {
  event: "INSERT" | "UPDATE";
  /** The row's status after the change; null when the payload carries none. */
  status: string | null;
};

/** The end states of an ai_chat_turns row. */
const TERMINAL_AI_TURN_STATUSES: ReadonlySet<string> = new Set<AiChatTurnStatus>([
  "completed",
  "failed",
  "cancelled",
]);

/** CR-199: whether a realtime change moved a turn to an end state. */
export function isTerminalAiTurnChange(change: AiTurnChange): boolean {
  return (
    change.event === "UPDATE" && !!change.status && TERMINAL_AI_TURN_STATUSES.has(change.status)
  );
}

function readTurnStatus(record: unknown): string | null {
  if (typeof record !== "object" || record === null || !("status" in record)) return null;
  return typeof record.status === "string" ? record.status : null;
}

/**
 * CR-141: realtime changes of a room's AI turns. The `room_id=eq.` filter is
 * load-bearing: DELETE events bypass RLS and carry only the primary key, so
 * the callback only reacts to INSERT and UPDATE (never DELETE). Only the
 * granted status columns reach a browser: realtime honours the column grants
 * of migration 20260926170000_cr141_ai_chat_turns.sql.
 *
 * `channelName` must be unique per concurrent subscriber: supabase-js returns
 * the EXISTING channel for a repeated topic (see createTasksSubscription). The
 * chat keeps the default; comment threads pass their own (CR-195).
 */
export function createAiTurnSubscription(
  roomId: string,
  onChange: (change: AiTurnChange) => void,
  channelName = `ai-chat-turns-${roomId}`,
): RealtimeChannel {
  return supabase
    .channel(channelName)
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "ai_chat_turns", filter: `room_id=eq.${roomId}` },
      (payload) => onChange({ event: "INSERT", status: readTurnStatus(payload.new) }),
    )
    .on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "ai_chat_turns", filter: `room_id=eq.${roomId}` },
      (payload) => onChange({ event: "UPDATE", status: readTurnStatus(payload.new) }),
    )
    .subscribe();
}
